// Firebase User Data Service - Replaces ALL localStorage with live Firebase persistence
import { doc, setDoc, getDoc, updateDoc, collection, addDoc, getDocs, deleteDoc, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';

export class FirebaseUserDataService {
  
  // Get user document reference
  static getUserDocRef(userId) {
    return doc(db, 'users', userId);
  }

  // Get user data collection reference  
  static getUserDataRef(userId, collection) {
    return doc(db, 'userData', `${userId}_${collection}`);
  }

  // Initialize user data document if it doesn't exist
  static async initializeUserData(userId) {
    try {
      const userRef = this.getUserDocRef(userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        await setDoc(userRef, {
          userId,
          createdAt: new Date(),
          lastActive: new Date()
        });
      }
    } catch (error) {
      console.error('Error initializing user data:', error);
    }
  }

  // ===== API KEYS MANAGEMENT =====
  static async saveAPIKeys(userId, apiKeys) {
    try {
      const apiKeysRef = this.getUserDataRef(userId, 'apiKeys');
      await setDoc(apiKeysRef, {
        apiKeys: apiKeys,
        updatedAt: new Date()
      });
      toast.success('API keys saved to cloud!');
    } catch (error) {
      console.error('Error saving API keys:', error);
      toast.error('Failed to save API keys to cloud');
      throw error;
    }
  }

  static async getAPIKeys(userId) {
    try {
      const apiKeysRef = this.getUserDataRef(userId, 'apiKeys');
      const doc = await getDoc(apiKeysRef);
      return doc.exists() ? doc.data().apiKeys : [];
    } catch (error) {
      console.error('Error loading API keys:', error);
      return [];
    }
  }

  // ===== BUDGET CONTROL MANAGEMENT =====
  static async saveBudgetSettings(userId, budgetData) {
    try {
      const budgetRef = this.getUserDataRef(userId, 'budget');
      await setDoc(budgetRef, {
        scrapingBudget: budgetData.scrapingBudget,
        currentBudgetUsage: budgetData.currentBudgetUsage,
        budgetPeriod: budgetData.budgetPeriod || 'monthly',
        updatedAt: new Date()
      });
      toast.success('Budget settings saved to cloud!');
    } catch (error) {
      console.error('Error saving budget settings:', error);
      toast.error('Failed to save budget settings');
      throw error;
    }
  }

  static async getBudgetSettings(userId) {
    try {
      // 🛡️ DEFENSIVE CHECK: Ensure userId is valid
      if (!userId || userId === 'undefined' || typeof userId !== 'string') {
        console.warn('FirebaseUserDataService.getBudgetSettings: Invalid userId provided:', userId);
        return {
          scrapingBudget: 100,
          currentBudgetUsage: 0,
          budgetPeriod: 'monthly'
        };
      }

      const budgetRef = this.getUserDataRef(userId, 'budget');
      const doc = await getDoc(budgetRef);
      
      if (doc.exists()) {
        return doc.data();
      } else {
        // Return default budget settings
        return {
          scrapingBudget: 100,
          currentBudgetUsage: 0,
          budgetPeriod: 'monthly'
        };
      }
    } catch (error) {
      console.error('Error loading budget settings:', error);
      // Return sensible defaults on any error
      return {
        scrapingBudget: 100,
        currentBudgetUsage: 0,
        budgetPeriod: 'monthly'
      };
    }
  }

  // ===== CAMPAIGNS MANAGEMENT =====
  static async saveCampaigns(userId, campaigns) {
    try {
      const campaignsRef = this.getUserDataRef(userId, 'campaigns');
      await setDoc(campaignsRef, {
        campaigns: campaigns,
        updatedAt: new Date()
      });
      toast.success('Campaigns saved to cloud!');
    } catch (error) {
      console.error('Error saving campaigns:', error);
      toast.error('Failed to save campaigns');
      throw error;
    }
  }

  static async getCampaigns(userId) {
    try {
      const campaignsRef = this.getUserDataRef(userId, 'campaigns');
      const doc = await getDoc(campaignsRef);
      return doc.exists() ? doc.data().campaigns : [];
    } catch (error) {
      console.error('Error loading campaigns:', error);
      return [];
    }
  }

  // ===== USER PREFERENCES =====
  static async saveUserPreferences(userId, preferences) {
    try {
      const prefsRef = this.getUserDataRef(userId, 'preferences');
      await setDoc(prefsRef, {
        ...preferences,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving user preferences:', error);
      throw error;
    }
  }

  static async getUserPreferences(userId) {
    try {
      const prefsRef = this.getUserDataRef(userId, 'preferences');
      const doc = await getDoc(prefsRef);
      
      if (doc.exists()) {
        return doc.data();
      } else {
        // Return default preferences
        return {
          theme: 'light',
          defaultAIProvider: 'openai',
          emailSignature: 'Best regards,\nThe MarketGenie Team'
        };
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
      return {
        theme: 'light',
        defaultAIProvider: 'openai',
        emailSignature: 'Best regards,\nThe MarketGenie Team'
      };
    }
  }

  // ===== REAL-TIME SYNC UTILITIES =====
  static async syncUserData(userId) {
    try {
      // Update last active timestamp
      const userRef = this.getUserDocRef(userId);
      await updateDoc(userRef, {
        lastActive: new Date()
      });
    } catch (error) {
      console.error('Error syncing user data:', error);
    }
  }

  // ===== INTEGRATION CREDENTIALS =====
  static async saveIntegrationCredentials(userId, integrationId, credentials) {
    try {
      const integRef = this.getUserDataRef(userId, `integration_${integrationId}`);
      await setDoc(integRef, {
        credentials: credentials,
        integrationId: integrationId,
        updatedAt: new Date()
      });
      toast.success(`${integrationId} credentials saved to cloud!`);
    } catch (error) {
      console.error('Error saving integration credentials:', error);
      toast.error('Failed to save integration credentials');
      throw error;
    }
  }

  static async getIntegrationCredentials(userId, integrationId) {
    try {
      const integRef = this.getUserDataRef(userId, `integration_${integrationId}`);
      const doc = await getDoc(integRef);
      return doc.exists() ? doc.data().credentials : null;
    } catch (error) {
      console.error('Error loading integration credentials:', error);
      return null;
    }
  }

  // ===== WORKFLOW AUTOMATION =====
  static async saveWorkflows(userId, workflowData) {
    try {
      const workflowRef = this.getUserDataRef(userId, 'workflows');
      await setDoc(workflowRef, {
        ...workflowData,
        updatedAt: new Date()
      });
      console.log('Workflows saved to Firebase successfully');
    } catch (error) {
      console.error('Error saving workflows:', error);
      throw error;
    }
  }

  static async getWorkflows(userId) {
    try {
      const workflowRef = this.getUserDataRef(userId, 'workflows');
      const doc = await getDoc(workflowRef);
      
      if (doc.exists()) {
        return doc.data();
      } else {
        // Return default empty workflows
        return {
          workflows: [],
          templates: []
        };
      }
    } catch (error) {
      console.error('Error loading workflows:', error);
      return {
        workflows: [],
        templates: []
      };
    }
  }

  // ===== CRM SYSTEM =====
  static async saveCRMContacts(userId, contactsData) {
    try {
      const contactsRef = this.getUserDataRef(userId, 'crm_contacts');
      await setDoc(contactsRef, {
        ...contactsData,
        updatedAt: new Date()
      });
      console.log('CRM Contacts saved to Firebase successfully');
    } catch (error) {
      console.error('Error saving CRM contacts:', error);
      throw error;
    }
  }

  static async getCRMContacts(userId) {
    try {
      const contactsRef = this.getUserDataRef(userId, 'crm_contacts');
      const doc = await getDoc(contactsRef);
      
      if (doc.exists()) {
        return doc.data();
      } else {
        return {
          contacts: []
        };
      }
    } catch (error) {
      console.error('Error loading CRM contacts:', error);
      return {
        contacts: []
      };
    }
  }

  static async saveCRMDeals(userId, dealsData) {
    try {
      const dealsRef = this.getUserDataRef(userId, 'crm_deals');
      await setDoc(dealsRef, {
        ...dealsData,
        updatedAt: new Date()
      });
      console.log('CRM Deals saved to Firebase successfully');
    } catch (error) {
      console.error('Error saving CRM deals:', error);
      throw error;
    }
  }

  static async getCRMDeals(userId) {
    try {
      const dealsRef = this.getUserDataRef(userId, 'crm_deals');
      const doc = await getDoc(dealsRef);
      
      if (doc.exists()) {
        return doc.data();
      } else {
        return {
          deals: []
        };
      }
    } catch (error) {
      console.error('Error loading CRM deals:', error);
      return {
        deals: []
      };
    }
  }

  static async saveCRMFunnels(userId, funnelsData) {
    try {
      const funnelsRef = this.getUserDataRef(userId, 'crm_funnels');
      await setDoc(funnelsRef, {
        ...funnelsData,
        updatedAt: new Date()
      });
      console.log('CRM Funnels saved to Firebase successfully');
    } catch (error) {
      console.error('Error saving CRM funnels:', error);
      throw error;
    }
  }

  static async getCRMFunnels(userId) {
    try {
      const funnelsRef = this.getUserDataRef(userId, 'crm_funnels');
      const doc = await getDoc(funnelsRef);
      
      if (doc.exists()) {
        return doc.data();
      } else {
        return {
          funnels: []
        };
      }
    } catch (error) {
      console.error('Error loading CRM funnels:', error);
      return {
        funnels: []
      };
    }
  }

  static async saveWhiteLabelFunnels(userId, funnelsData) {
    try {
      const funnelsRef = this.getUserDataRef(userId, 'white_label_funnels');
      await setDoc(funnelsRef, {
        ...funnelsData,
        updatedAt: new Date()
      });
      console.log('White-label funnels saved to Firebase successfully');
    } catch (error) {
      console.error('Error saving white-label funnels:', error);
      throw error;
    }
  }

  static async getWhiteLabelFunnels(userId) {
    try {
      const funnelsRef = this.getUserDataRef(userId, 'white_label_funnels');
      const doc = await getDoc(funnelsRef);
      
      if (doc.exists()) {
        return doc.data();
      } else {
        return {
          funnels: []
        };
      }
    } catch (error) {
      console.error('Error loading white-label funnels:', error);
      return {
        funnels: []
      };
    }
  }

  // ===== GENERIC DATA METHODS =====
  static async getUserData(userId, collection) {
    try {
      const dataRef = this.getUserDataRef(userId, collection);
      const doc = await getDoc(dataRef);
      
      if (doc.exists()) {
        return {
          success: true,
          data: doc.data()
        };
      } else {
        return {
          success: true,
          data: null
        };
      }
    } catch (error) {
      console.error(`Error loading ${collection}:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  static async saveUserData(userId, collection, data) {
    try {
      const dataRef = this.getUserDataRef(userId, collection);
      await setDoc(dataRef, {
        ...data,
        updatedAt: new Date()
      });
      return {
        success: true
      };
    } catch (error) {
      console.error(`Error saving ${collection}:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ===== UNIFIED CONTACTS METHOD =====
  static async getContacts(userId, tenantId = null) {
    // Use tenantId if provided, otherwise use userId for backwards compatibility
    const id = tenantId || userId;
    return this.getUserData(id, 'crm_contacts');
  }

  // ===== BUSINESS PROFILE MANAGEMENT =====
  static async saveBusinessProfile(tenantId, profile) {
    try {
      const profileRef = this.getUserDataRef(tenantId, 'businessProfile');
      await setDoc(profileRef, {
        ...profile,
        updatedAt: new Date()
      });
      return {
        success: true
      };
    } catch (error) {
      console.error('Error saving business profile:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  static async getBusinessProfile(tenantId) {
    try {
      const profileRef = this.getUserDataRef(tenantId, 'businessProfile');
      const doc = await getDoc(profileRef);
      
      if (doc.exists()) {
        return doc.data();
      }
      return null;
    } catch (error) {
      console.error('Error getting business profile:', error);
      return null;
    }
  }

  // ===== CALENDAR CONNECTIONS MANAGEMENT =====
  static async saveCalendarConnections(tenantId, connections) {
    try {
      console.log('🔥 FirebaseUserDataService: Saving calendar connections for tenant:', tenantId);
      console.log('🔥 Data being saved:', connections);
      
      const connectionsRef = this.getUserDataRef(tenantId, 'calendarConnections');
      const dataToSave = {
        connections: connections,
        updatedAt: new Date()
      };
      
      console.log('🔥 Full document being saved:', dataToSave);
      await setDoc(connectionsRef, dataToSave);
      console.log('✅ Calendar connections saved to cloud successfully!');
    } catch (error) {
      console.error('❌ Error saving calendar connections:', error);
      throw error;
    }
  }

  // ==========================================
  // DEAL MANAGEMENT
  // ==========================================

  /**
   * Get all deals for a tenant
   */
  static async getDeals(tenantId) {
    try {
      console.log('🔥 FirebaseUserDataService: Loading deals for tenant:', tenantId)
      
      const dealsCollection = collection(db, 'tenants', tenantId, 'deals')
      const dealsSnapshot = await getDocs(dealsCollection)
      
      const deals = []
      dealsSnapshot.forEach((doc) => {
        deals.push({ id: doc.id, ...doc.data() })
      })
      
      console.log(`✅ Loaded ${deals.length} deals`)
      return deals
    } catch (error) {
      console.error('❌ Error getting deals:', error)
      throw error
    }
  }

  /**
   * Get a single deal by ID
   */
  static async getDeal(tenantId, dealId) {
    try {
      const dealRef = doc(db, 'tenants', tenantId, 'deals', dealId)
      const dealDoc = await getDoc(dealRef)
      
      if (dealDoc.exists()) {
        return { id: dealDoc.id, ...dealDoc.data() }
      }
      return null
    } catch (error) {
      console.error('❌ Error getting deal:', error)
      throw error
    }
  }

  /**
   * Create a new deal
   */
  static async createDeal(tenantId, dealData) {
    try {
      const dealsCollection = collection(db, 'tenants', tenantId, 'deals')
      
      const newDeal = {
        ...dealData,
        createdAt: new Date().toISOString(),
        movedToStageAt: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
        daysInStage: 0,
        emailsSent: 0,
        emailsOpened: 0,
        emailsClicked: 0,
        emailsReplied: 0,
        meetingsScheduled: 0,
        meetingsCompleted: 0,
        proposalsSent: 0,
        proposalsOpened: false,
        notes: [],
        tags: dealData.tags || [],
        probability: dealData.probability || 30,
        tenantId
      }
      
      const docRef = await addDoc(dealsCollection, newDeal)
      console.log('✅ Deal created:', docRef.id)
      
      return { id: docRef.id, ...newDeal }
    } catch (error) {
      console.error('❌ Error creating deal:', error)
      throw error
    }
  }

  /**
   * Update an existing deal
   */
  static async updateDeal(tenantId, dealId, updates) {
    try {
      const dealRef = doc(db, 'tenants', tenantId, 'deals', dealId)
      
      await updateDoc(dealRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      
      console.log('✅ Deal updated:', dealId)
      return true
    } catch (error) {
      console.error('❌ Error updating deal:', error)
      throw error
    }
  }

  /**
   * Delete a deal
   */
  static async deleteDeal(tenantId, dealId) {
    try {
      const dealRef = doc(db, 'tenants', tenantId, 'deals', dealId)
      await deleteDoc(dealRef)
      
      console.log('✅ Deal deleted:', dealId)
      return true
    } catch (error) {
      console.error('❌ Error deleting deal:', error)
      throw error
    }
  }

  /**
   * Get deals by stage
   */
  static async getDealsByStage(tenantId, stage) {
    try {
      const dealsCollection = collection(db, 'tenants', tenantId, 'deals')
      const q = query(dealsCollection, where('stage', '==', stage))
      const querySnapshot = await getDocs(q)
      
      const deals = []
      querySnapshot.forEach((doc) => {
        deals.push({ id: doc.id, ...doc.data() })
      })
      
      return deals
    } catch (error) {
      console.error('❌ Error getting deals by stage:', error)
      throw error
    }
  }

  /**
   * Get stuck deals (in stage for 7+ days)
   */
  static async getStuckDeals(tenantId) {
    try {
      const deals = await this.getDeals(tenantId)
      return deals.filter(deal => 
        deal.daysInStage >= 7 && 
        deal.stage !== 'closed_won' && 
        deal.stage !== 'closed_lost'
      )
    } catch (error) {
      console.error('❌ Error getting stuck deals:', error)
      throw error
    }
  }

  static async getCalendarConnections(tenantId) {
    try {
      console.log('🔥 FirebaseUserDataService: Loading calendar connections for tenant:', tenantId);
      
      const connectionsRef = this.getUserDataRef(tenantId, 'calendarConnections');
      const doc = await getDoc(connectionsRef);
      
      console.log('🔥 Document exists:', doc.exists());
      
      if (doc.exists()) {
        const docData = doc.data();
        console.log('🔥 Raw document data:', docData);
        console.log('🔥 Connections data:', docData.connections);
        return docData.connections;
      }
      console.log('❌ No calendar connections document found');
      return null;
    } catch (error) {
      console.error('❌ Error getting calendar connections:', error);
      return null;
    }
  }

  // ==========================================
  // RECENT LEADS MANAGEMENT (Staging Area)
  // ==========================================

  /**
   * Get all recent leads for a tenant (staging area before contact management)
   */
  static async getRecentLeads(tenantId) {
    try {
      console.log('🔥 FirebaseUserDataService: Loading recent leads for tenant:', tenantId);
      
      const leadsRef = collection(db, 'recent_leads');
      const q = query(leadsRef, where('tenantId', '==', tenantId), orderBy('scrapedAt', 'desc'));
      const leadsSnapshot = await getDocs(q);
      
      const leads = [];
      leadsSnapshot.forEach((doc) => {
        leads.push({ id: doc.id, ...doc.data() });
      });
      
      console.log(`✅ Loaded ${leads.length} recent leads`);
      return leads;
    } catch (error) {
      console.error('❌ Error getting recent leads:', error);
      throw error;
    }
  }

  /**
   * Add a new recent lead to the staging area
   */
  static async addRecentLead(leadData, tenantId) {
    try {
      const leadsRef = collection(db, 'recent_leads');
      
      const newLead = {
        ...leadData,
        tenantId,
        scrapedAt: new Date().toISOString(),
        validationStatus: leadData.validationStatus || 'pending',
        transferredToContacts: false,
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(leadsRef, newLead);
      console.log('✅ Recent lead added:', docRef.id);
      
      return { id: docRef.id, ...newLead };
    } catch (error) {
      console.error('❌ Error adding recent lead:', error);
      throw error;
    }
  }

  /**
   * Add multiple recent leads in bulk (batch operation)
   */
  static async addRecentLeadsBulk(leads, tenantId) {
    try {
      console.log(`📦 Adding ${leads.length} leads in bulk...`);
      const batch = writeBatch(db);
      const leadsRef = collection(db, 'recent_leads');
      
      const addedLeads = [];
      
      for (const leadData of leads) {
        const newDocRef = doc(leadsRef);
        const newLead = {
          ...leadData,
          tenantId,
          scrapedAt: new Date().toISOString(),
          validationStatus: leadData.validationStatus || 'pending',
          transferredToContacts: false,
          createdAt: new Date().toISOString()
        };
        
        batch.set(newDocRef, newLead);
        addedLeads.push({ id: newDocRef.id, ...newLead });
      }
      
      await batch.commit();
      console.log(`✅ Bulk added ${addedLeads.length} recent leads`);
      
      return addedLeads;
    } catch (error) {
      console.error('❌ Error bulk adding recent leads:', error);
      throw error;
    }
  }

  /**
   * Update a recent lead (e.g., validation status)
   */
  static async updateRecentLead(leadId, updates, tenantId) {
    try {
      const leadRef = doc(db, 'recent_leads', leadId);
      
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(leadRef, updateData);
      console.log('✅ Recent lead updated:', leadId);
      
      return true;
    } catch (error) {
      console.error('❌ Error updating recent lead:', error);
      throw error;
    }
  }

  /**
   * Delete a recent lead
   */
  static async deleteRecentLead(leadId, tenantId) {
    try {
      const leadRef = doc(db, 'recent_leads', leadId);
      await deleteDoc(leadRef);
      console.log('✅ Recent lead deleted:', leadId);
      
      return true;
    } catch (error) {
      console.error('❌ Error deleting recent lead:', error);
      throw error;
    }
  }

  /**
   * Get recent leads by validation status
   */
  static async getRecentLeadsByStatus(tenantId, validationStatus) {
    try {
      const leadsRef = collection(db, 'recent_leads');
      const q = query(
        leadsRef, 
        where('tenantId', '==', tenantId),
        where('validationStatus', '==', validationStatus),
        orderBy('scrapedAt', 'desc')
      );
      const leadsSnapshot = await getDocs(q);
      
      const leads = [];
      leadsSnapshot.forEach((doc) => {
        leads.push({ id: doc.id, ...doc.data() });
      });
      
      return leads;
    } catch (error) {
      console.error('❌ Error getting leads by status:', error);
      throw error;
    }
  }

  /**
   * Archive old leads (older than 7 days and already transferred)
   */
  static async archiveOldLeads(tenantId) {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const leadsRef = collection(db, 'recent_leads');
      const q = query(
        leadsRef,
        where('tenantId', '==', tenantId),
        where('transferredToContacts', '==', true),
        where('scrapedAt', '<', sevenDaysAgo.toISOString())
      );
      
      const leadsSnapshot = await getDocs(q);
      const batch = writeBatch(db);
      
      leadsSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
      console.log(`✅ Archived ${leadsSnapshot.size} old leads`);
      
      return leadsSnapshot.size;
    } catch (error) {
      console.error('❌ Error archiving old leads:', error);
      throw error;
    }
  }
}

export default FirebaseUserDataService;